import {
  Input,
  Table,
  TableColumn,
  useModal,
  useToast,
} from '@keymax-dev/smartepi-ui';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  WithActiveDetails,
  WithActiveDetailsContentProps,
} from '../../../components/details-section';
import PageActions from '../../../components/page-actions';
import { Role, User } from '../../../domain/models';
import { useRestItemDelete } from '../../../hooks/use-rest-item-delete';
import useRestList from '../../../hooks/use-rest-list';
import { EndPoints, RouterPaths } from '../../../utils/constants';
import { getErrorMessage } from '../../../utils/error-messages';
import AddEmployeeRoleModal from './add-employee';
import { RoleDetailsElement } from './style';

const Strings = {
  Buttons: {
    DeleteRole: 'Excluir Função',
    AddEmployee: 'Adicionar Funcionário',
  },
  Inputs: {
    RoleName: 'Nome da Função',
    Search: 'Buscar funcionário...',
  },
  Table: {
    ColumnTitles: {
      Identification: 'ID',
      Employee: 'Funcionário',
    },
    Messages: {
      EmptyEmployees: 'Não há funcionários na função.',
    },
  },
  Messages: {
    UnknownError:
      'Ocorreu um erro inesperado. Por favor, verifique sua conexão com a internet.',
    Caution: 'Atenção!',
    DeleteConfirmation: 'Você deseja mesmo deletar a função?',
  },
};

const RoleDetails = (
  props: WithActiveDetailsContentProps<Role.Data>
): JSX.Element => {
  // Hooks
  const {
    currentState: [role, setRole, updateRoleValue],
    loading: updateLoading,
    requestError,
  } = props.restUpdate;
  const restList = useRestList<User.Data>(EndPoints.Users, {
    requestFilters: [{ filter: 'role', filterValue: role._id }],
  });

  const errorToast = useToast(<span />, { color: 'danger' });

  const addEmployeeModal = useModal(<AddEmployeeRoleModal roleId={role._id} />);

  // Handlers
  const tableScrollHandler = (
    event: React.UIEvent<HTMLTableSectionElement>
  ): void => {
    if (
      event.currentTarget.offsetHeight + event.currentTarget.scrollTop >=
      event.currentTarget.scrollHeight - 30
    ) {
      restList.nextBookmark();
    }
  };
  const addEmployeeClickHandler = (): void => {
    addEmployeeModal.injectProps({ roleId: role._id });
    addEmployeeModal.onClose((reason) => {
      if (typeof reason === 'object') {
        const newUser = reason as User.Data;
        restList.updateItems([newUser]);
      }
    });
    addEmployeeModal.open();
  };
  const { deleteClickHandler } = useRestItemDelete({
    endpoint: EndPoints.Roles,
    item: role,
    routerPath: RouterPaths.Roles,
    title: Strings.Messages.Caution,
    message: Strings.Messages.DeleteConfirmation,
  });

  // Page Elements
  const pageActions = [
    {
      text: Strings.Buttons.DeleteRole,
      icon: 'trash',
      onClick: deleteClickHandler,
    },
    {
      text: Strings.Buttons.AddEmployee,
      icon: 'accountPlus',
      onClick: addEmployeeClickHandler,
    },
  ];

  // Effects
  useEffect(() => {
    if (requestError) {
      errorToast.setContent(getErrorMessage(requestError.error));
      setTimeout(() => errorToast.open());
    }
  }, [requestError]);

  return (
    <RoleDetailsElement>
      <header className="__title-header">
        <Input
          disabled={updateLoading}
          iconRight="pencil"
          invert
          onBlur={(event) =>
            updateRoleValue({ name: event.currentTarget.value })
          }
          onChange={(event) => setRole({ ...role, name: event.target.value })}
          onKeyDown={(event) =>
            event.key === 'Enter' &&
            updateRoleValue({ name: event.currentTarget.value })
          }
          value={role.name}
          placeholder={Strings.Inputs.RoleName}
        />
        <PageActions actions={pageActions} invert />
      </header>
      <nav>
        <Input
          containerType="outline"
          iconRight="magnify"
          placeholder={Strings.Inputs.Search}
          containerProps={{ className: '__search-bar' }}
          invert
          onBlur={(event) => restList.search(event.target.value)}
          onKeyPress={(event) =>
            event.key === 'Enter' && restList.search(event.currentTarget.value)
          }
          disabled={restList.loading}
        />
      </nav>
      <Table
        data={restList.list}
        loading={restList.loading}
        config={{
          emptyMessage: Strings.Table.Messages.EmptyEmployees,
          onScroll: tableScrollHandler,
        }}
      >
        <TableColumn
          name={Strings.Table.ColumnTitles.Identification}
          key="identification"
          minwidth={window.innerWidth > 600 ? '100px' : '75px'}
        >
          {(item: User.Data): JSX.Element => <div>{item.identification}</div>}
        </TableColumn>
        <TableColumn
          name={Strings.Table.ColumnTitles.Employee}
          key="name"
          flex={1}
          minwidth="220px"
        >
          {(item: User.Data): JSX.Element => <div>{item.name}</div>}
        </TableColumn>
      </Table>
    </RoleDetailsElement>
  );
};

export default (): JSX.Element =>
  WithActiveDetails(RoleDetails, {
    id: useParams<{ id: string }>().id,
    endpoint: EndPoints.Role,
    cleanRouterPath: RouterPaths.Role(),
  });
